import { useState, useEffect, useRef, Suspense } from 'react'
import { AxiosError } from 'axios'
import { Flex } from '@radix-ui/themes'
import { useLoaderData, redirect, useNavigate } from 'react-router'
import { parseAsArrayOf, parseAsString, useQueryState, createLoader } from 'nuqs'
import { createClient, JSONAPIResponse } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { Me } from '@/api-client/Me'
import { SubscriptionSchedules } from '~/api-client/SubscriptionSchedules'
import { Addresses } from '@/api-client/Addresses'
import { Cards } from '@/api-client/Cards'
import { SubscriptionSchedule, Card, SubscriptionInventory, FlowerCollectionProduct } from '@/api-client/data-contracts'
import { ScheduleSelector } from '~/components/subscription/checkout/schedule-selector'
import { EstimatedDelivery } from '~/components/subscription/checkout/estimated-delivery'
import { NutrientModal } from '@/components/subscription/lp/nutrient-modal'
import { CreditCardForm } from '~/components/subscription/checkout/credit-card-form'
import { MazzoToggle } from '~/components/subscription/checkout/mazzo-toggle'
import { PriceTable } from '~/components/subscription/checkout/price-table'
import { AboutSubscription } from '~/components/subscription/checkout/about-subscription'
import { GiftNote } from '~/components/subscription/checkout/gift-note'
import { PageHeading } from '~/components/subscription/page-heading'
import { FormLabel } from '~/components/subscription/form-label'
import { Button } from '~/components/subscription/button'
import { styles } from './styles.css'
import nutrient from './images/nutrient.jpg'
import warn from './images/warn.svg'

const loadSearchParams = createLoader({ inventory_ids: parseAsArrayOf(parseAsString).withDefault([]) })

export const loader = async ({ request }) => {
  const account = await fetchMe().catch(() => ({ data: { data: null } }))
  const schedules = await fetchSchedules()
  const addresses = await fetchAddresses().catch(() => ({ data: { data: [] } }))
  const {
    data: { included },
  } = schedules as unknown as JSONAPIResponse<typeof fetchSchedules>
  const subscriptions = await fetchSubscriptions().catch(() => ({ data: { data: [] } }))
  const inBulkPurchasables = await fetchInBulkPurchasables()
  const { inventory_ids: inventoryIds } = loadSearchParams(request)
  const inventories = included.filter((item) => item.type === 'subscription_inventory' && inventoryIds.includes(item.id))

  if (inventories.length === 0) {
    const inventory = included.find((item) => item.type === 'subscription_inventory' && !item.attributes.is_soldout)

    if (inventory) {
      return redirect(`/subscription/new?inventory_ids=${inventory.id}`)
    } else {
      return redirect('/subscription')
    }
  }

  return [account, schedules, addresses, inBulkPurchasables, subscriptions]
}

function fetchMe() {
  return createClient(Me).getMe()
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, upcoming: '1' })
}

function fetchAddresses() {
  return createClient(Addresses).getAddresses()
}

function findSchedule(schedules: SubscriptionSchedule[], inventoryId: string) {
  return schedules.find((schedule) => schedule.relationships.inventory.data.id === inventoryId)
}

function fetchInBulkPurchasables() {
  return createClient(Subscriptions).getSubscriptionsInBulkPurchasables()
}

function fetchDefaultCard() {
  return createClient(Cards).getCardsDefault()
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions()
}

const creditCardPromise = fetchDefaultCard()
  .then((res) => {
    const { data } = (res as unknown as JSONAPIResponse<typeof fetchDefaultCard>).data

    if (data && !data.attributes.is_expired) {
      return data
    } else {
      return null
    }
  })
  .catch(() => null)

function createSubscriptionAddress(address) {
  const params = {
    kind: address.with_sender_name ? 'gift' : 'home',
    sender_name: address.with_sender_name ? address.sender_name : '',
    postal_code: address.postal_code,
    state: address.state,
    city_and_street: address.city_and_street,
    others: address.others,
    name: address.name,
    phone_number: address.phone_number,
  }

  return createClient(Subscriptions).postSubscriptionsAddresses(params)
}

export const SubscriptionNew: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: loaderSchedules, included },
    },
    {
      data: { data: addresses },
    },
    {
      data: { data: inBulkPurchasables },
    },
    {
      data: { data: subscriptions },
    },
  ] = useLoaderData() as [
    JSONAPIResponse<typeof fetchMe>,
    JSONAPIResponse<typeof fetchSchedules>,
    JSONAPIResponse<typeof fetchAddresses>,
    JSONAPIResponse<typeof fetchInBulkPurchasables>,
    JSONAPIResponse<typeof fetchSubscriptions>
  ]

  const addressDetail = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const cityAndStreetInput = useRef<HTMLInputElement>(null)
  const mazzo = inBulkPurchasables.find((p) => p.attributes.name.match(/mazzo/) && p.attributes.name.match(/クリア/) && p.attributes.on_sale)
  const [inventoryIds, setInventoryIds] = useQueryState('inventory_ids', parseAsArrayOf(parseAsString).withDefault([]))
  const availableInventories = included.filter(
    (item) => item.type === 'subscription_inventory' && !item.attributes.is_soldout
  ) as unknown as SubscriptionInventory[]
  const inventories = included.filter((item) => item.type === 'subscription_inventory' && inventoryIds.includes(item.id)) as unknown as SubscriptionInventory[]
  const upcomingInventory = inventories.find((i) => !i.attributes.is_soldout)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [streetError, setStreetError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [deliveryDays, setDeliveryDays] = useState([])
  const [schedules, setSchedules] = useState(loaderSchedules)
  const [schedule, setSchedule] = useState(() => findSchedule(schedules, upcomingInventory?.id))
  const [delayedWeek, setDelayedWeek] = useState(0)
  const [creditCard, setCreditCard] = useState<Card | null>(null)
  const [holderName, setHolderName] = useState('')
  const [numberElement, setNumberElement] = useState(null)
  const [email, setEmail] = useState(account ? account.attributes.email : '')
  const [password, setPassword] = useState('')
  const [inBulkOrderable, setInBulkOrderable] = useState<FlowerCollectionProduct | null>(null)
  const [address, setAddress] = useState(() => {
    const primary = addresses.find((a) => a.attributes.is_primary)

    if (primary) {
      return {
        ...primary.attributes,
        with_sender_name: !!primary.attributes.sender_name,
      }
    } else {
      return {
        name: '',
        postal_code: '',
        state: '',
        city_and_street: '',
        others: '',
        phone_number: '',
        with_sender_name: false,
        sender_name: '',
      }
    }
  })
  const inventoryOptions = [[availableInventories[0]], [availableInventories[0], availableInventories[2]], availableInventories].filter(
    (option) => option !== null
  )

  const isAlreadySubscribed = subscriptions.length > 0

  useEffect(() => {
    if (!addressDetail.current) return

    if (deliveryDays.length === 1) {
      addressDetail.current.style.height = 'auto'
    } else {
      addressDetail.current.style.height = '0px'
    }
  }, [deliveryDays.length])

  useEffect(() => {
    schedule?.attributes?.deliver_at && setDeliveryDays(schedule.attributes.deliver_at.map((ts) => new Date(ts * 1000)))
  }, [schedule?.attributes?.deliver_at])

  useEffect(() => {
    const { postal_code } = address

    if (postal_code.length < 7) return
    if (!upcomingInventory) return

    fetchSchedules(postal_code).then((response) => {
      const { data: schedules, included } = (response as unknown as JSONAPIResponse<typeof fetchSchedules>).data
      const schedule = findSchedule(schedules, upcomingInventory.id)
      setSchedules(schedules)
      setSchedule(schedule)

      const delayId = schedule.relationships.delay.data?.id
      const delay = included.find((item) => item.type === 'subscription_schedule_delay' && item.id === delayId)
      const days = delay?.attributes?.days || 0
      setDelayedWeek(Math.ceil(days / 7))
    })

    if (!address.city_and_street) {
      const code = postal_code.replace('-', '')
      window
        .fetch(`https://joe-noh.github.io/jp-postal-code-api/api/v1/${code}.json`)
        .then((res) => res.json())
        .then((json) => {
          const addr = json['addresses'][0]['ja']

          if (addr) {
            setAddress((prev) => ({
              ...prev,
              state: addr['prefecture'],
              city_and_street: addr['address1'] + addr['address2'],
            }))
          }
        })
        .catch(() => {}) // 補完に失敗しても無視
    }
  }, [address.postal_code, upcomingInventory?.id])

  const handleChangeAddress = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    setAddress((prev) => ({ ...prev, [name]: value }))
  }

  const handleUpdateCard = ({ holderName, numberElement }) => {
    setHolderName(holderName)
    setNumberElement(numberElement)
  }

  const handleChangeMazzo = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mazzo) return

    if (e.target.checked) {
      setInBulkOrderable(mazzo)
    } else {
      setInBulkOrderable(null)
    }
  }

  const validateCityAndStreet = () => {
    const input = cityAndStreetInput.current

    if (input && !input.value.match(/[０-９\d]/)) {
      const message = '番地が記載されていません。ご確認ください'

      input.setCustomValidity(message)
      setStreetError(message)
    } else {
      input.setCustomValidity('')
      setStreetError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    validateCityAndStreet()

    if (!isSubmitting) {
      setIsSubmitting(true)

      try {
        if (!account) {
          await createClient(Subscriptions).postSubscriptionsSessions({ email, password, signup: '1' })
        }

        const newAddress = (await createSubscriptionAddress(address)) as unknown as JSONAPIResponse<typeof createSubscriptionAddress>

        const formData = new FormData()
        formData.append('address_id', newAddress.data.data.id)
        inventories.forEach((inventory) => formData.append('inventory_ids[]', inventory.id))
        formData.append('payment_method', 'credit_card')

        if (inBulkOrderable) {
          formData.append('in_bulk_orderable_id', inBulkOrderable.id)
        }

        if (creditCard) {
          const response = await window.fetch('/subscription/checkout', {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: {
              'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content,
            },
          })

          if (response.ok) {
            navigate('/subscription/subscribed')
          } else {
            const { error } = await response.json()

            setErrorMessage(error.message_displayable)
          }
        } else {
          const res = await window.payjpInstance.createToken(numberElement, {
            three_d_secure: true,
            card: { email, name: holderName },
          })

          if ('id' in res) {
            formData.append('card_token', res.id)

            const response = await window.fetch('/subscription/checkout', {
              method: 'POST',
              body: formData,
              credentials: 'include',
              headers: {
                'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content,
              },
            })

            if (response.ok) {
              const { tds_url } = await response.json()

              location.href = tds_url
            } else {
              const { error } = await response.json()

              setErrorMessage(error.message_displayable)
            }
          }
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response.data.error.message_displayable || '申し込みに失敗しました。入力内容をご確認ください')
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <article className={styles.screenContainer}>
      <form className={styles.pageContainer} onSubmit={handleSubmit}>
        <Flex direction="column" gap="48px">
          <Flex direction="column" align="center" gap="32px" width="100%">
            <PageHeading>お申し込み手続き</PageHeading>
            <Flex direction="column" gap="24px" width="100%">
              <NutrientModal renderTrigger={() => <img src={nutrient} className={styles.nutrientBanner} />} />
              <Flex direction="column" align="start" gap="16px" width="100%">
                <h2 className={styles.formHeading}>お届け先</h2>
                <Flex direction="column" align="start" gap="8px" width="100%" className={styles.addressContainer}>
                  <Flex direction="column" align="start" gap="4px" width="100%">
                    <FormLabel htmlFor="name">お名前</FormLabel>
                    <input type="text" id="name" name="name" required className={styles.input} value={address.name} onChange={handleChangeAddress} />
                  </Flex>
                  <Flex direction="column" align="start" gap="4px" width="100%">
                    <FormLabel htmlFor="postal-code">郵便番号</FormLabel>
                    <input
                      type="text"
                      id="postal-code"
                      name="postal_code"
                      required
                      className={styles.input}
                      value={address.postal_code}
                      onChange={handleChangeAddress}
                    />
                  </Flex>
                  <Flex direction="column" align="start" gap="8px" width="100%" ref={addressDetail}>
                    <Flex direction="column" align="start" gap="4px" width="100%">
                      <label htmlFor="state">都道府県</label>
                      <select id="state" name="state" required className={styles.input} value={address.state} onChange={handleChangeAddress}>
                        <option value="北海道">北海道</option>
                        <option value="青森県">青森県</option>
                        <option value="岩手県">岩手県</option>
                        <option value="宮城県">宮城県</option>
                        <option value="秋田県">秋田県</option>
                        <option value="山形県">山形県</option>
                        <option value="福島県">福島県</option>
                        <option value="茨城県">茨城県</option>
                        <option value="栃木県">栃木県</option>
                        <option value="群馬県">群馬県</option>
                        <option value="埼玉県">埼玉県</option>
                        <option value="千葉県">千葉県</option>
                        <option value="東京都">東京都</option>
                        <option value="神奈川県">神奈川県</option>
                        <option value="新潟県">新潟県</option>
                        <option value="富山県">富山県</option>
                        <option value="石川県">石川県</option>
                        <option value="福井県">福井県</option>
                        <option value="山梨県">山梨県</option>
                        <option value="長野県">長野県</option>
                        <option value="岐阜県">岐阜県</option>
                        <option value="静岡県">静岡県</option>
                        <option value="愛知県">愛知県</option>
                        <option value="三重県">三重県</option>
                        <option value="滋賀県">滋賀県</option>
                        <option value="京都府">京都府</option>
                        <option value="大阪府">大阪府</option>
                        <option value="兵庫県">兵庫県</option>
                        <option value="奈良県">奈良県</option>
                        <option value="和歌山県">和歌山県</option>
                        <option value="鳥取県">鳥取県</option>
                        <option value="島根県">島根県</option>
                        <option value="岡山県">岡山県</option>
                        <option value="広島県">広島県</option>
                        <option value="山口県">山口県</option>
                        <option value="徳島県">徳島県</option>
                        <option value="香川県">香川県</option>
                        <option value="愛媛県">愛媛県</option>
                        <option value="高知県">高知県</option>
                        <option value="福岡県">福岡県</option>
                        <option value="佐賀県">佐賀県</option>
                        <option value="長崎県">長崎県</option>
                        <option value="熊本県">熊本県</option>
                        <option value="大分県">大分県</option>
                        <option value="宮崎県">宮崎県</option>
                        <option value="鹿児島県">鹿児島県</option>
                        <option value="沖縄県" disabled>
                          沖縄県
                        </option>
                      </select>
                    </Flex>
                    <Flex direction="column" align="start" gap="4px" width="100%">
                      <FormLabel htmlFor="city-and-street">市区町村・番地</FormLabel>
                      <input
                        type="text"
                        id="city-and-street"
                        name="city_and_street"
                        required
                        className={styles.input}
                        value={address.city_and_street}
                        onChange={handleChangeAddress}
                        onBlur={validateCityAndStreet}
                        ref={cityAndStreetInput}
                      />
                      {streetError && <p className={styles.inputError}>{streetError}</p>}
                    </Flex>
                    <Flex direction="column" align="start" gap="4px" width="100%">
                      <FormLabel htmlFor="others">建物名・部屋番号</FormLabel>
                      <input type="text" id="others" name="others" className={styles.input} value={address.others} onChange={handleChangeAddress} />
                    </Flex>
                    <Flex direction="column" align="start" gap="4px" width="100%">
                      <FormLabel htmlFor="phone-number">電話番号</FormLabel>
                      <input
                        type="text"
                        id="phone-number"
                        name="phone_number"
                        required
                        className={styles.input}
                        value={address.phone_number}
                        onChange={handleChangeAddress}
                      />
                    </Flex>
                    <label className={styles.checkbox}>
                      <Flex direction="row" align="center" gap="4px">
                        <input
                          type="checkbox"
                          name="with_sender_name"
                          checked={address.with_sender_name}
                          onChange={(e) => setAddress((prev) => ({ ...prev, with_sender_name: e.target.checked }))}
                        />
                        <span>注文者と受取人が異なる</span>
                      </Flex>
                    </label>
                    {address.with_sender_name && (
                      <Flex direction="column" align="start" gap="8px" width="100%">
                        <Flex direction="column" align="start" gap="4px" width="100%">
                          <FormLabel htmlFor="sender-name">ご注文者様のお名前</FormLabel>
                          <input
                            type="text"
                            id="sender-name"
                            name="sender_name"
                            required={address.with_sender_name}
                            className={styles.input}
                            value={address.sender_name}
                            onChange={handleChangeAddress}
                          />
                        </Flex>
                        <GiftNote />
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
          <Flex direction="column" align="start" gap="16px" width="100%">
            <Flex direction="column" align="start" gap="4px" width="100%">
              <h2 className={styles.formHeading}>お届けスケジュール</h2>
              <p className={styles.formSubheading}>受け取り曜日・頻度は、あとから変更可能です</p>
            </Flex>
            <Flex direction="column" align="center" gap="24px" width="100%">
              <ScheduleSelector schedules={schedules} selectedInventories={inventories} onChange={(ids) => setInventoryIds(ids)} />
              <EstimatedDelivery schedules={schedules} selectedInventories={inventories} delayedWeek={delayedWeek} />
            </Flex>
          </Flex>
          {account ? null : (
            <Flex direction="column" align="start" gap="16px" width="100%">
              <Flex direction="column" align="start" gap="8px" width="100%">
                <h2 className={styles.formHeading}>会員登録</h2>
                <Flex direction="row" align="start" gap="2px" width="100%">
                  <img src={warn} className={styles.warnIcon} />
                  <p className={styles.warn}>すでにFLOWERのアカウントをお持ちの方は、登録メールアドレスとパスワードをご入力ください</p>
                </Flex>
              </Flex>
              <Flex direction="column" align="start" gap="8px" width="100%">
                <Flex direction="column" align="start" gap="4px" width="100%">
                  <FormLabel htmlFor="email">メールアドレス</FormLabel>
                  <input type="email" id="email" required className={styles.input} value={email} onChange={(e) => setEmail(e.target.value)} />
                </Flex>
                <Flex direction="column" align="start" gap="4px" width="100%">
                  <FormLabel htmlFor="password">パスワード（半角英数字6文字以上）</FormLabel>
                  <input
                    type="password"
                    id="password"
                    minLength={6}
                    required
                    className={styles.input}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Flex>
              </Flex>
            </Flex>
          )}
          <Flex direction="column" align="start" gap="16px" width="100%">
            <h2 className={styles.formHeading}>お支払い</h2>
            <Suspense fallback={<></>}>
              <CreditCardForm creditCard={creditCardPromise} onInitialized={setCreditCard} onUpdate={handleUpdateCard} />
            </Suspense>
          </Flex>
          {mazzo && <MazzoToggle checked={!!inBulkOrderable} onChange={handleChangeMazzo} />}
          <PriceTable inBulkOrderble={inBulkOrderable} />
          <AboutSubscription />
          <Flex direction="column" gap="16px" width="100%">
            <Flex direction="column" align="center" gap="16px" width="100%">
              <p className={styles.legal}>
                <a href="/terms">利用規約</a>に同意して
              </p>
              <Button as="button" type="submit" fluid={true} disabled={isSubmitting || isAlreadySubscribed}>
                {isAlreadySubscribed ? 'すでに定期便をご利用中です' : '申し込みを確定する'}
              </Button>
              {errorMessage && <p className={styles.checkoutError}>{errorMessage}</p>}
              <p className={styles.legal}>
                <a href="/commercial">注文キャンセルに関する注意事項</a>
              </p>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </article>
  )
}
